import logoSrc from "../assets/beyondsure-logo.png";

/**
 * Beyond Sure — brand lockup.
 *
 * Uses the REAL PNG downloaded from www.beyondsure.in. This is the only
 * acceptable rendering of the mark: green shield + bold "BeyondSure" wordmark
 * ("Beyond" in deep forest, "Sure" in spring green) + tagline "Click. Cover.
 * Relax." It is NOT re-rendered as a serif, NOT recoloured, NOT generated.
 *
 * Variants:
 *   "full"    →  the PNG as-is, for use on Paper surfaces (form panel)
 *   "white"   →  CSS-filter inverts every non-transparent pixel to pure white,
 *                used on the Deep-Forest left panel on desktop where the
 *                two-tone green would otherwise disappear against the
 *                green background
 */
type Variant = "full" | "white";

interface LockupProps {
  /** Color variant. Defaults to "full" (real two-tone PNG). */
  variant?: Variant;
  /** Pixel height. The PNG aspect is roughly 4.5:1 — width scales from this. */
  height?: number;
  /** Optional className for layout positioning. */
  className?: string;
}

export function Lockup({
  variant = "full",
  height = 40,
  className = "",
}: LockupProps) {
  // `brightness(0) invert(1)` first crushes all non-transparent pixels to
  // black, then inverts to pure white — yielding a clean monochrome silhouette
  // that preserves the exact shield + wordmark + tagline lockup shape.
  const whiteFilter =
    variant === "white" ? { filter: "brightness(0) invert(1)" } : undefined;

  return (
    <img
      src={logoSrc}
      alt="BeyondSure · Click. Cover. Relax."
      height={height}
      // width omitted intentionally — the natural aspect ratio of the PNG
      // (~4.5:1) handles itself when only height is constrained.
      className={["inline-block select-none", className].join(" ")}
      style={{ height, width: "auto", ...whiteFilter }}
      draggable={false}
    />
  );
}

export default Lockup;
